import { Icon } from '@chakra-ui/react';
import {
  Box,
  BoxProps,
  Button,
  ChakraSelect,
  FormControl,
  FormLabel,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useCustomToast,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useUploadFileApi } from 'apis/config/use-upload-file-api';
import {
  useCreateDealProductApi,
  useGetDealCategoryTreeApi,
  useGetExclusiveDealApi,
  useUpdateDealProductApi,
  useUpdateDealProductCategoryApi,
  useUpdateProductOverrideApi,
} from 'apis/exclusive-deals';
import { EMPTY_UOM_OPTIONS, useGetUomOptionsApi } from 'apis/master-products';
import { dictionaryLabelFor, dictionaryValues, useGetAttributeDictionaryApi } from 'apis/product-attributes';
import { findCategoryPath } from 'pages/exclusive-deals/components/editable-deals-grid/category-tree-helpers';
import type {
  CategoryRef,
  ProductOption,
} from 'pages/exclusive-deals/components/editable-deals-grid/editable-deal-row.types';
import { GridCategoryModal } from 'pages/exclusive-deals/components/editable-deals-grid/grid-category-modal';
import { buildCanonicalOptions, toCanonicalValue } from 'pages/exclusive-deals/utils/canonical-options';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiUpload } from 'react-icons/fi';

type TInputs = {
  nameEn: string;
  brandEn: string;
  defaultUnit: string;
  imageUrl: string;
  specs: string;
  code: string;
  origin: string;
  state: string;
};

const defaultValues: TInputs = {
  nameEn: '',
  brandEn: '',
  defaultUnit: '',
  imageUrl: '',
  specs: '',
  code: '',
  origin: '',
  state: '',
};

export interface IEditProductInfoModalCell extends BoxProps {
  trigger: JSX.Element;
  variant?: 'edit' | 'create';
  deal?: any; // edit mode: deal-backed when set (uses the deal-scoped endpoints)
  supplierId?: number; // required in create mode and in deal-less edit mode
  product?: ProductOption | null; // edit mode without a deal: baseline values (new grid rows)
  onCreated?: (product: ProductOption) => void; // called after successful create
  onUpdated?: (fields: Partial<ProductOption>) => void; // called after successful edit
}

export const EditProductInfoModal = ({
  trigger,
  variant = 'edit',
  deal,
  supplierId,
  product: rowProduct,
  onCreated,
  onUpdated,
}: IEditProductInfoModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useCustomToast();
  const isCreate = variant === 'create';
  // Edit mode comes in two flavors: deal-backed (existing grid rows — deal-scoped endpoints)
  // and product-backed (new rows with no deal yet — product-addressed endpoint).
  const isDealEdit = !isCreate && !!deal?.id;

  const { data: dealDetailsData, isLoading: isDealLoading } = useGetExclusiveDealApi(deal?.id, {
    enabled: isOpen && isDealEdit,
  });
  const dealDetails = dealDetailsData?.data;

  const { mutate: updateProduct, isLoading: isUpdating } = useUpdateProductOverrideApi();
  const { mutate: updateCategory, isLoading: isUpdatingCategory } = useUpdateDealProductCategoryApi();
  const { mutate: updateSupplierProduct, isLoading: isUpdatingSupplierProduct } = useUpdateDealProductApi();
  const { mutate: createProduct, isLoading: isCreating } = useCreateDealProductApi();
  const { mutate: uploadFile, isLoading: isUploading } = useUploadFileApi();

  const { register, control, handleSubmit, reset, setValue, getValues, watch } = useForm<TInputs>({
    mode: 'onBlur',
    defaultValues: { ...defaultValues },
  });

  // The catalog category is bound to the product (not the deal), so it's picked here.
  const [category, setCategory] = useState<CategoryRef | null>(null);

  const { data: dictionaryData } = useGetAttributeDictionaryApi();
  const dictionary = dictionaryData?.data;
  const originValues = dictionaryValues(dictionary, 'origin');
  const stateValues = dictionaryValues(dictionary, 'state');

  const { data: uomData, isError: isUomError } = useGetUomOptionsApi();
  const uomOptions = uomData ?? EMPTY_UOM_OPTIONS;

  // The deal-detail response carries only catalogCategoryId; resolve the display label
  // from the cached category tree (React Query dedupes with the picker modal's fetch).
  const { data: categoryTreeData } = useGetDealCategoryTreeApi({ enabled: isOpen });
  const categoryLabel = useMemo(() => {
    if (!category) return '';
    if (category.title) return category.title;
    const path = findCategoryPath(categoryTreeData?.data ?? [], category.id);
    return path.length ? path.map((n) => n.title || `Untitled (${n.id})`).join(' › ') : `#${category.id}`;
  }, [categoryTreeData, category]);

  const imageUrl = watch('imageUrl');

  // Populate form with current product values: from the deal-detail fetch when deal-backed,
  // or from the row's product snapshot when the row has no deal yet (product-backed).
  useEffect(() => {
    if (!isOpen || isCreate) return;
    const product = isDealEdit ? dealDetails?.product : rowProduct;
    if (!product) return;
    setValue('nameEn', product.nameEn ?? '');
    setValue('brandEn', product.brandEn ?? '');
    setValue(
      'defaultUnit',
      uomOptions.length ? toCanonicalValue(uomOptions, product.defaultUnit ?? '') : product.defaultUnit ?? ''
    );
    setValue('imageUrl', product.imageUrl ?? '');
    setValue('specs', product.specs ?? '');
    // The attribute dictionary loads async — if it hasn't resolved yet, keep the raw
    // value (never blocked/cleared) and let the re-normalization effect below fix it
    // up once the dictionary arrives. defaultUnit is guarded the same way on
    // uomOptions.length, for the same reason: the UOM options endpoint is its own
    // independent query with its own load latency.
    setValue('origin', dictionary ? toCanonicalValue(originValues, product.origin ?? '') : product.origin ?? '');
    setValue('state', dictionary ? toCanonicalValue(stateValues, product.state ?? '') : product.state ?? '');
    setCategory(
      product.catalogCategoryId != null ? { id: product.catalogCategoryId, title: product.categoryTitle ?? '' } : null
    );
    // dictionary/originValues/stateValues intentionally excluded — this effect loads the
    // product snapshot into the form; re-normalizing origin/state once the dictionary
    // resolves is handled by the effect below so it doesn't also re-load nameEn etc.
    // (No react-hooks eslint plugin is configured in this repo, so no disable directive
    // is needed here.)
  }, [isOpen, isCreate, isDealEdit, dealDetails, rowProduct, setValue]);

  // Re-normalize origin/state once the dictionary resolves after the form was already
  // populated with raw values above. Reads current form values via getValues (not the
  // product snapshot) so it never clobbers an edit the user made in the meantime —
  // toCanonicalValue is idempotent on values that are already canonical or genuinely
  // legacy, so this is safe to run once per dictionary load.
  useEffect(() => {
    if (!isOpen || isCreate || !dictionary) return;
    setValue('origin', toCanonicalValue(originValues, getValues('origin')));
    setValue('state', toCanonicalValue(stateValues, getValues('state')));
    // originValues/stateValues/setValue/getValues intentionally excluded from deps: this
    // should fire once per dictionary load, not on every render.
  }, [dictionary, isOpen, isCreate]);

  // Separate from the dictionary effect above on purpose: the UOM options and the attribute
  // dictionary are independent queries with different retry behavior (the dictionary sets
  // retry: false as an optional enhancement; UOM options keeps default retries because
  // defaultUnit is required). Gating defaultUnit's re-normalization on `dictionary` would tie
  // its correctness to an unrelated query that can fail permanently — see the mechanism note
  // in the finding this fixes. Same idempotency rationale as the effect above.
  useEffect(() => {
    if (!isOpen || isCreate || !uomOptions.length) return;
    setValue('defaultUnit', toCanonicalValue(uomOptions, getValues('defaultUnit')));
  }, [uomOptions, isOpen, isCreate]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    uploadFile(
      { body: formData },
      {
        onSuccess: (data) => {
          setValue('imageUrl', data?.data?.url || data?.url || '');
        },
        onError: () => {
          if (fileInputRef.current) fileInputRef.current.value = '';
        },
      }
    );
  };

  const onSubmit = (data: TInputs) => {
    if (isCreate) {
      if (!supplierId) {
        toast({ description: 'Select a supplier before creating a product', status: 'error' });
        return;
      }
      if (!data.nameEn.trim()) {
        toast({ description: 'English name is required', status: 'error' });
        return;
      }
      if (!data.code.trim()) {
        toast({ description: 'Code is required', status: 'error' });
        return;
      }
      if (!data.defaultUnit.trim()) {
        toast({ description: 'Unit is required', status: 'error' });
        return;
      }
      if (!category) {
        toast({ description: 'Category is required', status: 'error' });
        return;
      }
      const body = {
        nameEn: data.nameEn,
        code: data.code,
        brandEn: data.brandEn || undefined,
        defaultUnit: data.defaultUnit,
        specs: data.specs || undefined,
        imageUrl: data.imageUrl || undefined,
        origin: data.origin || undefined,
        state: data.state || undefined,
      };
      createProduct(
        { supplierId, body },
        {
          onSuccess: (res: any) => {
            const rawId = res?.id ?? res?.data?.id;
            const newId = rawId != null ? Number(rawId) : NaN;
            if (!Number.isFinite(newId) || newId <= 0) {
              toast({
                description: 'Product created. Reopen the product dropdown to select it.',
                status: 'success',
              });
              onCloseModal();
              return;
            }
            // The create endpoint doesn't persist the category — it rides along on the
            // grid row and the bulk-upsert save applies it to the product.
            onCreated?.({
              id: newId,
              nameEn: data.nameEn,
              brandEn: data.brandEn || '',
              defaultUnit: data.defaultUnit,
              specs: data.specs || '',
              origin: data.origin || '',
              state: data.state || '',
              catalogCategoryId: category.id,
              categoryTitle: category.title,
              imageUrl: data.imageUrl || '',
            });
            onCloseModal();
          },
        }
      );
      return;
    }

    const product = isDealEdit ? dealDetails?.product : rowProduct;
    const body: Record<string, any> = {};

    if (data.nameEn && data.nameEn !== (product?.nameEn ?? '')) body.nameEn = data.nameEn;
    if (data.brandEn !== (product?.brandEn ?? '')) body.brandEn = data.brandEn;
    if (data.defaultUnit && data.defaultUnit !== (product?.defaultUnit ?? '')) body.defaultUnit = data.defaultUnit;
    if (data.imageUrl !== (product?.imageUrl ?? '')) body.imageUrl = data.imageUrl;
    if (data.specs !== (product?.specs ?? '')) body.specs = data.specs;
    if (data.origin !== (product?.origin ?? '')) body.origin = data.origin;
    if (data.state !== (product?.state ?? '')) body.state = data.state;

    const categoryChanged = !!category && category.id !== (product?.catalogCategoryId ?? null);

    if (Object.keys(body).length === 0 && !categoryChanged) {
      toast({ description: 'No changes to save', status: 'success' });
      return;
    }

    const finish = () => {
      onUpdated?.({
        nameEn: data.nameEn,
        brandEn: data.brandEn,
        defaultUnit: data.defaultUnit,
        specs: data.specs,
        origin: data.origin,
        state: data.state,
        catalogCategoryId: category?.id ?? product?.catalogCategoryId ?? null,
        categoryTitle: category?.title ?? product?.categoryTitle ?? '',
        ...(isDealEdit ? {} : { imageUrl: data.imageUrl }),
      });
      onCloseModal();
    };

    if (!isDealEdit) {
      // Product-backed (new grid row): fields save via the product-addressed endpoint.
      // The category has no API call here — it rides the row and the bulk-upsert Save
      // applies it to the product (same contract as the inline create flow).
      if (!supplierId || !rowProduct) {
        toast({ description: 'Select a supplier and product first', status: 'error' });
        return;
      }
      if (Object.keys(body).length > 0) {
        updateSupplierProduct({ supplierId, productId: rowProduct.id, body }, { onSuccess: finish });
      } else {
        finish();
      }
      return;
    }

    const runCategoryUpdate = () => {
      if (categoryChanged && category) {
        updateCategory({ dealId: deal?.id, body: { catalogCategoryId: category.id } }, { onSuccess: finish });
      } else {
        finish();
      }
    };

    if (Object.keys(body).length > 0) {
      updateProduct({ dealId: deal?.id, body }, { onSuccess: runCategoryUpdate });
    } else {
      runCategoryUpdate();
    }
  };

  const onCloseModal = () => {
    onClose();
    reset(defaultValues);
    setCategory(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const product = isDealEdit ? dealDetails?.product : rowProduct;

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onCloseModal} closeOnOverlayClick={false} size='xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isCreate ? 'Create Product' : 'Edit Product Information'}
            {!isCreate && product?.nameEn && (
              <Text fontSize='sm' fontWeight='normal' color='gray.500'>
                {product.nameEn}
              </Text>
            )}
          </ModalHeader>

          <ModalBody>
            {isDealEdit && isDealLoading ? (
              <HStack justify='center' py='lg'>
                <Spinner size='md' />
                <Text color='gray.500'>Loading deal details...</Text>
              </HStack>
            ) : (
              <Stack as='form' id='editProductInfoForm' spacing='lg' onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Image */}
                <HStack spacing='md' align='center'>
                  {imageUrl ? (
                    <Image
                      src={imageUrl}
                      alt='Product'
                      boxSize='64px'
                      objectFit='cover'
                      borderRadius='md'
                      border='1px solid'
                      borderColor='gray.200'
                    />
                  ) : (
                    <Box
                      boxSize='64px'
                      borderRadius='md'
                      border='2px dashed'
                      borderColor='gray.300'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'>
                      <Text fontSize='xs' color='gray.400'>
                        No image
                      </Text>
                    </Box>
                  )}
                  <Button
                    size='sm'
                    variant='outline'
                    leftIcon={<Icon as={FiUpload} />}
                    isLoading={isUploading}
                    onClick={() => fileInputRef.current?.click()}>
                    Upload Image
                  </Button>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='image/*'
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                  {imageUrl && (
                    <Button size='xs' variant='ghost' colorScheme='red' onClick={() => setValue('imageUrl', '')}>
                      Remove
                    </Button>
                  )}
                </HStack>

                {/* Product Fields */}
                <FormControl isRequired={isCreate}>
                  <FormLabel>Name (EN)</FormLabel>
                  <Input {...register('nameEn')} placeholder='English name' />
                </FormControl>

                <FormControl>
                  <FormLabel>Brand (EN)</FormLabel>
                  <Input {...register('brandEn')} placeholder='English brand' />
                </FormControl>

                {isCreate && (
                  <FormControl isRequired>
                    <FormLabel>Code</FormLabel>
                    <Input {...register('code')} placeholder='ex. YC2122' />
                  </FormControl>
                )}

                <Controller
                  control={control}
                  name='defaultUnit'
                  render={({ field: { onChange, value, onBlur } }) => {
                    const options = buildCanonicalOptions(uomOptions, value);
                    const selected = options.find((o) => o.value === value) ?? null;
                    return (
                      <FormControl isRequired={isCreate}>
                        <FormLabel>Unit</FormLabel>
                        <ChakraSelect
                          options={options}
                          value={selected}
                          onChange={(opt: any) => onChange(opt?.value ?? '')}
                          onBlur={onBlur}
                          placeholder='Select unit'
                          isClearable={!isCreate}
                          menuPortalTarget={document.body}
                          menuPosition='fixed'
                          styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                        />
                        {isUomError && (
                          <Text fontSize='xs' color='red.500' mt='1'>
                            Couldn&apos;t load units — reload the page to try again.
                          </Text>
                        )}
                      </FormControl>
                    );
                  }}
                />

                <FormControl isRequired={isCreate}>
                  <FormLabel>Category</FormLabel>
                  <GridCategoryModal
                    value={category}
                    onSelect={setCategory}
                    trigger={
                      <Button size='sm' variant='outline' colorScheme='gray' w='fit-content'>
                        {category ? categoryLabel : 'Set category'}
                      </Button>
                    }
                  />
                </FormControl>

                <Controller
                  control={control}
                  name='origin'
                  render={({ field: { onChange, value, onBlur } }) => {
                    const options = buildCanonicalOptions(originValues, value);
                    const selected = options.find((o) => o.value === value) ?? null;
                    return (
                      <FormControl>
                        <FormLabel>Product Origin</FormLabel>
                        <ChakraSelect
                          options={options}
                          value={selected}
                          onChange={(opt: any) => onChange(opt?.value ?? '')}
                          onBlur={onBlur}
                          placeholder='Search country...'
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition='fixed'
                          styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </FormControl>
                    );
                  }}
                />

                <Controller
                  control={control}
                  name='state'
                  render={({ field: { onChange, value, onBlur } }) => {
                    const options = buildCanonicalOptions(stateValues, value, dictionaryLabelFor(dictionary, 'state'));
                    const selected = options.find((o) => o.value === value) ?? null;
                    return (
                      <FormControl>
                        <FormLabel>Product State</FormLabel>
                        <ChakraSelect
                          options={options}
                          value={selected}
                          onChange={(opt: any) => onChange(opt?.value ?? '')}
                          onBlur={onBlur}
                          placeholder='Select state'
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition='fixed'
                          styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </FormControl>
                    );
                  }}
                />

                <FormControl>
                  <FormLabel>Product Specs</FormLabel>
                  <Input {...register('specs')} maxLength={255} placeholder='e.g. 500g pack, 2L bottle' />
                </FormControl>
              </Stack>
            )}
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='outline' colorScheme='gray' onClick={onCloseModal}>
                Cancel
              </Button>
              <Button
                variant='solid'
                colorScheme='red'
                type='submit'
                form='editProductInfoForm'
                isLoading={isCreate ? isCreating : isUpdating || isUpdatingCategory || isUpdatingSupplierProduct}
                isDisabled={isDealEdit && isDealLoading}>
                {isCreate ? 'Create Product' : 'Save Changes'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
